import { useEffect, useRef } from 'react';

/**
 * Handwritten-style write-on for KaTeX output.
 *
 * KaTeX (rendered with `output:'html'`) lays the equation out as nested spans
 * whose DOM order matches how a person writes it on a board — numerator,
 * fraction bar, denominator, left to right. Revealing those leaf nodes in
 * order, with a pencil riding the active glyph, reads as live handwriting
 * without needing per-symbol stroke outlines.
 *
 * The hook mutates ONLY inline `visibility` on the rendered KaTeX nodes and
 * overlays one absolutely-positioned pencil; the equation's layout, size and
 * metrics stay byte-identical to the static render (no reflow on hand-off).
 *
 * IMPORTANT — re-render safety. The playback host (SlideStage) re-renders every
 * frame (annotations, spotlights, paused all change per frame) and hands this
 * element a FRESH `onWritingDone` callback each time. The effect therefore
 * depends ONLY on `[writing, html]` — the true triggers — and reads everything
 * else (onDone, paused, sizing) through refs. An earlier version listed `onDone`
 * in the deps, so every frame tore the animation down and re-hid every glyph
 * before the interval could reveal it: the equation stayed invisible for the
 * entire play. Do not add per-frame values to the dependency array.
 */
export function useLatexWriteOn(
  hostRef: React.RefObject<HTMLElement | null>,
  opts: {
    writing: boolean;
    paused?: boolean;
    fontSize: number;
    /** Rendered html — the effect re-arms when the equation itself changes. */
    html: string | null;
    onDone?: () => void;
    /** Glyphs per second (default 12 — brisk teacher writing). */
    glyphsPerSec?: number;
    /** Target total write duration (ms). When set, the reveal is paced to fill
     *  it (synced to the teacher's speaking pace) instead of a fixed rate. */
    durationMs?: number;
  },
) {
  const { writing, paused, fontSize, html, onDone, glyphsPerSec = 24, durationMs } = opts;

  // Everything the effect needs but must NOT restart on lives in a ref.
  const pausedRef = useRef(!!paused);
  pausedRef.current = !!paused;
  const onDoneRef = useRef<(() => void) | undefined>(onDone);
  onDoneRef.current = onDone;
  const sizeRef = useRef(fontSize);
  sizeRef.current = fontSize;
  const gpsRef = useRef(glyphsPerSec);
  gpsRef.current = glyphsPerSec;
  const durRef = useRef(durationMs);
  durRef.current = durationMs;

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !html) return undefined;

    // Reveal helper — clears any leftover hidden state from a prior run.
    const revealAll = () => {
      host.querySelectorAll<HTMLElement>('[data-lwo]').forEach((n) => {
        n.style.visibility = '';
        n.removeAttribute('data-lwo');
      });
    };

    if (!writing) {
      revealAll();
      return undefined;
    }

    // A reveal that happens while PAUSED is a seek/seed (scrub, or the resting
    // first frame before Play), not a live write — show it fully, no animation.
    // Reduced-motion users likewise get the finished equation instantly.
    const reducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (pausedRef.current || reducedMotion) {
      revealAll();
      onDoneRef.current?.();
      return undefined;
    }

    // Collect leaves in DOM (= writing) order from the VISIBLE render only
    // (`output:'html'` emits `.katex-html`; there's no MathML twin to skip, but
    // target it explicitly so a future output change can't feed us hidden nodes).
    const root =
      host.querySelector('.katex-html') ?? host.querySelector('.katex');
    if (!root) return undefined;
    const leaves: HTMLElement[] = [];
    const walk = (el: Element) => {
      if (el.tagName.toLowerCase() === 'svg') {
        leaves.push(el as unknown as HTMLElement); // sqrt radicals draw as one
        return;
      }
      if (el.children.length === 0) {
        const isBar = (el as HTMLElement).classList?.contains('frac-line');
        if (isBar || (el.textContent ?? '').trim()) leaves.push(el as HTMLElement);
        return;
      }
      for (const c of Array.from(el.children)) walk(c);
    };
    walk(root);
    if (!leaves.length) {
      onDoneRef.current?.();
      return undefined;
    }

    leaves.forEach((n) => {
      n.style.visibility = 'hidden';
      n.setAttribute('data-lwo', '');
    });

    // One pencil overlay for the whole write (geometry mirrors the chalk-text
    // pencil: graphite tip at the anchor, body angled up-right).
    const u = Math.max(14, sizeRef.current);
    const hw = u * 0.085;
    const pencil = document.createElement('div');
    pencil.style.cssText =
      'position:absolute;left:0;top:0;pointer-events:none;z-index:30;transition:transform 60ms linear;';
    pencil.innerHTML =
      `<svg width="${u * 2.8}" height="${u * 2.8}" viewBox="${-u * 1.4} ${-u * 2.6} ${u * 2.8} ${u * 2.8}" style="overflow:visible">` +
      `<g transform="rotate(28)">` +
      `<path d="M0 0 L ${-hw * 0.45} ${-u * 0.08} L ${hw * 0.45} ${-u * 0.08} Z" fill="#2b2b2b"/>` +
      `<path d="M ${-hw * 0.45} ${-u * 0.08} L ${-hw} ${-u * 0.2} L ${hw} ${-u * 0.2} L ${hw * 0.45} ${-u * 0.08} Z" fill="#e8c79a" stroke="#caa776" stroke-width="${u * 0.012}"/>` +
      `<rect x="${-hw}" y="${-u * 1.05}" width="${hw * 2}" height="${u * 0.85}" fill="#f5b301"/>` +
      `<rect x="${-hw}" y="${-u * 1.05}" width="${hw * 0.66}" height="${u * 0.85}" fill="#e0a200"/>` +
      `<rect x="${-hw}" y="${-u * 1.18}" width="${hw * 2}" height="${u * 0.13}" fill="#cfd3d6"/>` +
      `<rect x="${-hw}" y="${-u * 1.34}" width="${hw * 2}" height="${u * 0.16}" rx="${hw * 0.5}" fill="#e8788a"/>` +
      `</g></svg>`;
    const prevPos = host.style.position;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    // The katex host scrolls horizontally (overflow-x-auto); a pencil poking
    // past the box would otherwise spawn scrollbars or get chopped.
    const prevOverflow = host.style.overflow;
    host.style.overflow = 'visible';
    host.appendChild(pencil);

    // The stage renders under a CSS `transform: scale(s)` (ScaledStage), so
    // getBoundingClientRect returns SCALED screen px. `translate` applies in the
    // host's own (unscaled) space, so divide the screen delta by the scale.
    const movePencil = (n: HTMLElement) => {
      const hostBox = host.getBoundingClientRect();
      const s = host.offsetWidth > 0 ? hostBox.width / host.offsetWidth : 1;
      const b = n.getBoundingClientRect();
      const x = (b.right - hostBox.left) / (s || 1);
      const y = (b.bottom - hostBox.top) / (s || 1) - 1;
      pencil.style.transform = `translate(${x}px, ${y}px)`;
    };

    let i = 0;
    let stopped = false;
    // Pace to the teacher's speaking span when known (fill `durationMs` across
    // the glyph leaves, clamped so it never crawls or blurs); else fixed rate.
    // Floor 95ms/leaf = MAX write speed (user 2026-08-16): equations must never
    // flash by too fast to see. No ceiling on slowness beyond the 1500ms cap.
    const stepMs = durRef.current
      ? Math.max(95, Math.min(1500, durRef.current / leaves.length))
      : Math.max(95, 1000 / (gpsRef.current || 12));
    const finish = () => {
      window.clearInterval(timer);
      pencil.remove();
      host.style.overflow = prevOverflow;
      onDoneRef.current?.();
    };
    const timer = window.setInterval(() => {
      if (stopped || pausedRef.current) return; // freeze while paused
      const n = leaves[i];
      if (n) {
        n.style.visibility = '';
        n.removeAttribute('data-lwo');
        movePencil(n);
      }
      i += 1;
      if (i >= leaves.length) finish();
    }, stepMs);
    movePencil(leaves[0]);

    return () => {
      stopped = true;
      window.clearInterval(timer);
      pencil.remove();
      host.style.position = prevPos;
      host.style.overflow = prevOverflow;
      revealAll(); // never leave half-written math behind (slide change/unmount)
    };
    // ONLY [writing, html]. See the header note — anything per-frame here
    // reintroduces the invisible-equation regression.
  }, [writing, html, hostRef]);
}
